import {
  CartSettings,
  CookieSettings,
  Discount,
  FooterSettings,
  GeneralSettings,
  ProductVariantSelection,
  PromoSettings,
  SeoSettings,
  SocialLink,
} from '@data/sanity/schema'
import { SanityNewsletterBlock } from './blocks'
import { SanityBlogSettings } from './blog'
import { SanityComplexPortableText, SanitySimplePortableText } from './content'
import { SanityImageFragment } from './image'
import { SanityLinkFragment, SanityLinkReference } from './link'
import { SanityProductFragment } from './product'
import { SanityInstitutionItem } from './institution'
import { SanitySiteStrings } from './strings'
import { SanityPrintShopBase } from './shop'

export type SanityPromo = Pick<
  PromoSettings,
  'enabled' | 'display' | 'text'
> & {
  link?: SanityLinkReference
}

export type SanityMenuLinkFragment = SanityLinkFragment & {
  dropdownItems: SanityLinkFragment[]
}

export type SanityMenuFeaturedLinkFragment = SanityMenuLinkFragment & {
  featured?: SanityProductFragment[]
}

export interface SanityHeaderDesktopMenu {
  slug: string
  items: SanityMenuFeaturedLinkFragment[]
}

export interface SanityHeaderMobileMenu {
  slug: string
  items: SanityMenuLinkFragment[]
}

export interface SanityHeaderFragment {
  menuDesktopLeft?: SanityHeaderDesktopMenu
  menuDesktopRight?: SanityHeaderDesktopMenu
  menuMobilePrimary?: SanityHeaderMobileMenu
  menuMobileSecondary?: SanityHeaderMobileMenu
  promo?: SanityPromo
}

export interface SanityFooterBlockMenu {
  items: SanityLinkFragment[]
}

export interface SanityFooterBlock1 {
  content?: SanityComplexPortableText
  newsletter: SanityNewsletterBlock
}

export interface SanityFooterBlock2 {
  content?: SanityComplexPortableText
}

export interface SanityFooterBlock3 {
  menu: SanityFooterBlockMenu
}

export type SanityFooterSocialLink = Pick<
  SocialLink,
  'socialNetwork' | 'url'
> & {
  _key: string
}

export interface SanityFooterFragment {
  blocks?: [SanityFooterBlock1, SanityFooterBlock2, SanityFooterBlock3]
  social?: SanityFooterSocialLink[]
  copyright?: FooterSettings['copyright']
  paymentMethods?: SanityImageFragment[]
}

export interface SanityCartAdditionalInfoItem {
  icon?: SanityImageFragment
  title?: string
  description?: string
}

export type SanityCart = Pick<
  CartSettings,
  | 'storeURL'
  | 'taxRate'
  | 'message'
  | 'openInSeparatePage'
  | 'showVatId'
  | 'showComment'
> & {
  terms?: SanitySimplePortableText
  shipping: {
    schoolPickup?: boolean
    orderProcessingDay?: number
    deliveryTimeInDays?: number
  }
  additionalInfo?: SanityCartAdditionalInfoItem[]
}

export type SanityCartSettingsQuery = SanityCart | null

export interface SanityProductCount {
  slug: string
  count: number
}

export type SanityCookieConsent = Pick<
  CookieSettings,
  'enabled' | 'message'
> & {
  link?: SanityLinkReference
}

export type SanitySeo = Pick<
  SeoSettings,
  | 'siteTitle'
  | 'metaTitle'
  | 'metaDesc'
  | 'shareTitle'
  | 'shareDesc'
  | 'shareGraphic'
>

export interface SanityIdentity {
  logo?: SanityImageFragment
  invertedLogo?: SanityImageFragment
}

export type PublicSiteSettings = Pick<
  GeneralSettings,
  | 'siteURL'
  | 'klaviyoAccountID'
  | 'gtmContainerId'
  | 'analyticsId'
  | 'facebookEvents'
  | 'facebookPixelId'
  | 'facebookDomainVerification'
  | 'facebookTestEventCode'
  | 'stampedApiKey'
  | 'partnerAdsTracking'
  | 'pageAnimation'
>

export type AllSiteSettings = PublicSiteSettings &
  Pick<GeneralSettings, 'klaviyoPrivateKey' | 'stampedStoreHash'>

export enum SanityDiscountType {
  QUANTITY = 'quantity',
  BUNDLE = 'bundle',
}

export type SanityDiscountProduct = Pick<
  ProductVariantSelection['selection'],
  'id'
> & {
  variantIds: ProductVariantSelection['selection']['variants'][number]['id'][]
}

type SanityPartialDiscount = Pick<
  Discount,
  '_id' | 'title' | 'discountValuePercent'
>

export type SanityQuantityDiscount = SanityPartialDiscount & {
  type: SanityDiscountType.QUANTITY
  minimumQuantity: number
  maximumQuantity: number
}

export type SanityBundleDiscount = SanityPartialDiscount & {
  type: SanityDiscountType.BUNDLE
  products: SanityDiscountProduct[]
  doNotStackWithQuantityDiscounts?: boolean
}

export type SanityDiscount = SanityQuantityDiscount | SanityBundleDiscount

export type SanityDiscountQuery = SanityDiscount[]

export interface SanitySiteFragment {
  publicLocales: GeneralSettings['locale'][]
  settings: PublicSiteSettings
  identity?: SanityIdentity
  cart: SanityCart
  blogSettings: SanityBlogSettings | null
  productCounts: SanityProductCount[]
  cookieConsent?: SanityCookieConsent
  header?: SanityHeaderFragment
  footer?: SanityFooterFragment
  seo?: SanitySeo
  siteStrings?: SanitySiteStrings
  discounts: SanityDiscount[]
  institutions: SanityInstitutionItem[]
  shops: SanityPrintShopBase[]
}
